import type {
    Json
} from "../../services/backend/database.types.js";

import type {
    AdminContentEditorValue
} from "./adminContentEditor.js";


/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type HomeFeatureKind =
    | "news"
    | "daily_phrase"
    | "tip"
    | "grammar"
    | "vocabulary"
    | "culture"
    | "travel"
    | "announcement"
    | "other";


interface AdminHomeFeatureEditorValue {
    contentKey: string;

    kind: HomeFeatureKind;

    titleFr: string;
    titleFa: string;

    labelFr: string;
    labelFa: string;

    metaFr: string;
    metaFa: string;

    icon: string;

    href: string;

    position: string;

    featured: boolean;

    startsOn: string;
    endsOn: string;
}


interface AdminHomeFeatureValidationIssue {
    field: string;

    message: string;

    severity:
        | "error"
        | "warning";
}


/* -------------------------------------------------------------------------- */
/* Available kinds                                                            */
/* -------------------------------------------------------------------------- */

const homeFeatureKinds: readonly {
    value: HomeFeatureKind;
    label: string;
    defaultLabel: string;
    defaultIcon: string;
}[] = [
    {
        value: "news",
        label: "Actualité",
        defaultLabel: "ACTUALITÉ",
        defaultIcon: "📰"
    },
    {
        value: "daily_phrase",
        label: "Phrase du jour",
        defaultLabel: "PHRASE DU JOUR",
        defaultIcon: "💬"
    },
    {
        value: "tip",
        label: "Conseil",
        defaultLabel: "CONSEIL",
        defaultIcon: "💡"
    },
    {
        value: "grammar",
        label: "Grammaire",
        defaultLabel: "GRAMMAIRE",
        defaultIcon: "📖"
    },
    {
        value: "vocabulary",
        label: "Vocabulaire",
        defaultLabel: "VOCABULAIRE",
        defaultIcon: "🗣️"
    },
    {
        value: "culture",
        label: "Culture",
        defaultLabel: "CULTURE",
        defaultIcon: "🏛️"
    },
    {
        value: "travel",
        label: "Voyage",
        defaultLabel: "VOYAGE",
        defaultIcon: "✈️"
    },
    {
        value: "announcement",
        label: "Annonce",
        defaultLabel: "ANNONCE",
        defaultIcon: "📣"
    },
    {
        value: "other",
        label: "Autre",
        defaultLabel: "À DÉCOUVRIR",
        defaultIcon: "✨"
    }
];


/* -------------------------------------------------------------------------- */
/* Identity                                                                   */
/* -------------------------------------------------------------------------- */

const homeFeatureIdPattern =
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;


/* -------------------------------------------------------------------------- */
/* Read                                                                       */
/* -------------------------------------------------------------------------- */

function readAdminHomeFeatureEditor(
    editor: AdminContentEditorValue
): AdminHomeFeatureEditorValue {
    const payload =
        parseObject(
            editor.payloadText
        );

    const catalog =
        asObject(
            payload?.catalog
        );

    const document =
        asObject(
            payload?.document
        );

    const kind =
        readKind(
            document?.kind
            ?? catalog?.kind
        );

    const defaults =
        getKindDefaults(
            kind
        );

    return {
        contentKey:
            firstString(
                document?.id,
                catalog?.id,
                editor.contentKey
            ),

        kind,

        titleFr:
            firstString(
                document?.title,
                catalog?.title,
                editor.titleFr
            ),

        titleFa:
            firstString(
                document?.title_fa,
                catalog?.title_fa,
                editor.titleFa
            ),

        labelFr:
            firstString(
                document?.label,
                catalog?.label,
                defaults.defaultLabel
            ),

        labelFa:
            firstString(
                document?.label_fa,
                catalog?.label_fa
            ),

        metaFr:
            firstString(
                document?.meta,
                catalog?.meta
            ),

        metaFa:
            firstString(
                document?.meta_fa,
                catalog?.meta_fa
            ),

        icon:
            firstString(
                document?.icon,
                catalog?.icon,
                defaults.defaultIcon
            ),

        href:
            firstString(
                document?.href,
                catalog?.href
            ),

        position:
            numberString(
                document?.position
                ?? catalog?.position,
                "10"
            ),

        featured:
            booleanValue(
                document?.featured
                ?? catalog?.featured
            ),

        startsOn:
            firstString(
                document?.startsOn,
                catalog?.startsOn
            ),

        endsOn:
            firstString(
                document?.endsOn,
                catalog?.endsOn
            )
    };
}


/* -------------------------------------------------------------------------- */
/* Update                                                                     */
/* -------------------------------------------------------------------------- */

function updateAdminHomeFeatureEditor(
    editor: AdminContentEditorValue,
    feature: AdminHomeFeatureEditorValue
): AdminContentEditorValue {
    return {
        ...editor,

        contentKey:
            feature.contentKey,

        contentType:
            "home_feature",

        /*
         * A home card is editorial placement, not course content.
         * It therefore has no CEFR level.
         */
        level:
            "",

        payloadText:
            JSON.stringify(
                buildAdminHomeFeaturePayload(
                    feature
                ),
                null,
                2
            ),

        /*
         * home_feature schema starts at v1 independently from the
         * existing Grammar v2 schema.
         */
        schemaVersion:
            "1",

        sourcePath:
            editor.sourcePath
            || "admin-panel",

        titleFa:
            feature.titleFa,

        titleFr:
            feature.titleFr
    };
}


/* -------------------------------------------------------------------------- */
/* Build payload                                                              */
/* -------------------------------------------------------------------------- */

function buildAdminHomeFeaturePayload(
    feature: AdminHomeFeatureEditorValue
): Json {
    const shared:
        Record<string, Json | undefined> = {
            id:
                feature.contentKey.trim(),

            kind:
                feature.kind,

            title:
                feature.titleFr.trim(),

            title_fa:
                optionalText(
                    feature.titleFa
                ),

            label:
                feature.labelFr.trim(),

            label_fa:
                optionalText(
                    feature.labelFa
                ),

            meta:
                optionalText(
                    feature.metaFr
                ),

            meta_fa:
                optionalText(
                    feature.metaFa
                ),

            icon:
                feature.icon.trim(),

            href:
                optionalText(
                    feature.href
                ),

            position:
                parsePosition(
                    feature.position
                ),

            featured:
                feature.featured,

            startsOn:
                optionalText(
                    feature.startsOn
                ),

            endsOn:
                optionalText(
                    feature.endsOn
                )
        };

    const compacted =
        compactObject(
            shared
        );

    return {
        catalog: {
            ...compacted
        },

        document: {
            ...compacted
        }
    };
}


/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

function validateAdminHomeFeatureEditor(
    feature: AdminHomeFeatureEditorValue
): AdminHomeFeatureValidationIssue[] {
    const issues:
        AdminHomeFeatureValidationIssue[] = [];

    if (
        !homeFeatureIdPattern.test(
            feature.contentKey.trim()
        )
    ) {
        issues.push(
            errorIssue(
                "Identifiant",
                "Utilisez uniquement des minuscules, chiffres et tirets, par exemple phrase-ca-marche."
            )
        );
    }

    requireText(
        issues,
        "Titre français",
        feature.titleFr,
        2
    );

    requireText(
        issues,
        "Label français",
        feature.labelFr,
        2
    );

    requireText(
        issues,
        "Icône",
        feature.icon,
        1
    );

    if (
        !homeFeatureKinds.some(
            option =>
                option.value
                === feature.kind
        )
    ) {
        issues.push(
            errorIssue(
                "Type",
                "Le type de contenu éditorial est invalide."
            )
        );
    }

    const position =
        parseInteger(
            feature.position
        );

    if (
        position === null
        || position < 0
        || position > 999
    ) {
        issues.push(
            errorIssue(
                "Position",
                "La position doit être un entier compris entre 0 et 999."
            )
        );
    }

    if (
        feature.href.trim()
        && !isValidDestination(
            feature.href
        )
    ) {
        issues.push(
            errorIssue(
                "Destination",
                "Utilisez une route interne commençant par / ou une URL HTTPS."
            )
        );
    }

    if (
        feature.startsOn.trim()
        && !isIsoDate(
            feature.startsOn
        )
    ) {
        issues.push(
            errorIssue(
                "Visible à partir du",
                "Utilisez une date réelle au format AAAA-MM-JJ."
            )
        );
    }

    if (
        feature.endsOn.trim()
        && !isIsoDate(
            feature.endsOn
        )
    ) {
        issues.push(
            errorIssue(
                "Visible jusqu’au",
                "Utilisez une date réelle au format AAAA-MM-JJ."
            )
        );
    }

    if (
        isIsoDate(
            feature.startsOn
        )
        && isIsoDate(
            feature.endsOn
        )
        && feature.startsOn
            > feature.endsOn
    ) {
        issues.push(
            errorIssue(
                "Période d’affichage",
                "La date de fin doit être postérieure ou égale à la date de début."
            )
        );
    }

    /*
     * These fields are deliberately warnings:
     * a French-only card remains valid, but the admin sees immediately
     * that the Persian experience is incomplete.
     */

    if (
        !feature.titleFa.trim()
    ) {
        issues.push({
            field:
                "Titre persan",

            message:
                "Ajoutez le titre persan pour une carte entièrement bilingue.",

            severity:
                "warning"
        });
    }

    if (
        !feature.labelFa.trim()
    ) {
        issues.push({
            field:
                "Label persan",

            message:
                "Ajoutez le label persan pour une carte entièrement bilingue.",

            severity:
                "warning"
        });
    }

    return issues;
}


/* -------------------------------------------------------------------------- */
/* Kind helpers                                                               */
/* -------------------------------------------------------------------------- */

function getKindDefaults(
    kind: HomeFeatureKind
): {
    defaultLabel: string;
    defaultIcon: string;
} {
    const definition =
        homeFeatureKinds.find(
            option =>
                option.value
                === kind
        );

    return {
        defaultLabel:
            definition?.defaultLabel
            ?? "À DÉCOUVRIR",

        defaultIcon:
            definition?.defaultIcon
            ?? "✨"
    };
}


function readKind(
    value: Json | undefined
): HomeFeatureKind {
    if (
        typeof value === "string"
        && homeFeatureKinds.some(
            option =>
                option.value
                === value
        )
    ) {
        return value as HomeFeatureKind;
    }

    return "daily_phrase";
}


/* -------------------------------------------------------------------------- */
/* Public helpers for the UI                                                  */
/* -------------------------------------------------------------------------- */

function applyHomeFeatureKindDefaults(
    feature: AdminHomeFeatureEditorValue,
    kind: HomeFeatureKind
): AdminHomeFeatureEditorValue {
    const previousDefaults =
        getKindDefaults(
            feature.kind
        );

    const nextDefaults =
        getKindDefaults(
            kind
        );

    const labelWasDefault =
        !feature.labelFr.trim()
        || feature.labelFr
            === previousDefaults.defaultLabel;

    const iconWasDefault =
        !feature.icon.trim()
        || feature.icon
            === previousDefaults.defaultIcon;

    return {
        ...feature,

        kind,

        labelFr:
            labelWasDefault
                ? nextDefaults.defaultLabel
                : feature.labelFr,

        icon:
            iconWasDefault
                ? nextDefaults.defaultIcon
                : feature.icon
    };
}


function createEmptyAdminHomeFeature(
    contentKey = ""
): AdminHomeFeatureEditorValue {
    return {
        contentKey,

        kind:
            "daily_phrase",

        titleFr:
            "",

        titleFa:
            "",

        labelFr:
            "PHRASE DU JOUR",

        labelFa:
            "",

        metaFr:
            "",

        metaFa:
            "",

        icon:
            "💬",

        href:
            "",

        position:
            "10",

        featured:
            false,

        startsOn:
            "",

        endsOn:
            ""
    };
}


/* -------------------------------------------------------------------------- */
/* Generic validation helpers                                                 */
/* -------------------------------------------------------------------------- */

function requireText(
    issues: AdminHomeFeatureValidationIssue[],
    field: string,
    value: string,
    minimumLength: number
): void {
    if (
        value.trim().length
        < minimumLength
    ) {
        issues.push(
            errorIssue(
                field,
                `Ce champ doit contenir au moins ${minimumLength} caractère(s).`
            )
        );
    }
}


function errorIssue(
    field: string,
    message: string
): AdminHomeFeatureValidationIssue {
    return {
        field,
        message,
        severity:
            "error"
    };
}


/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

function isIsoDate(
    value: string
): boolean {
    if (
        !/^\d{4}-\d{2}-\d{2}$/u.test(
            value.trim()
        )
    ) {
        return false;
    }

    const normalized =
        value.trim();

    const date =
        new Date(
            `${normalized}T00:00:00Z`
        );

    return (
        !Number.isNaN(
            date.valueOf()
        )
        && date
            .toISOString()
            .slice(
                0,
                10
            )
            === normalized
    );
}


/* -------------------------------------------------------------------------- */
/* Navigation                                                                 */
/* -------------------------------------------------------------------------- */

function isValidDestination(
    value: string
): boolean {
    const normalized =
        value.trim();

    if (
        normalized.startsWith(
            "/"
        )
    ) {
        return (
            !normalized.startsWith(
                "//"
            )
        );
    }

    try {
        return (
            new URL(
                normalized
            ).protocol
            === "https:"
        );
    } catch {
        return false;
    }
}


/* -------------------------------------------------------------------------- */
/* Parsing helpers                                                            */
/* -------------------------------------------------------------------------- */

function parseObject(
    value: string
): Record<string, Json | undefined> | null {
    try {
        return asObject(
            JSON.parse(
                value
            ) as Json
        );
    } catch {
        return null;
    }
}


function asObject(
    value: Json | undefined
): Record<string, Json | undefined> | null {
    return (
        typeof value === "object"
        && value !== null
        && !Array.isArray(
            value
        )
    )
        ? value
        : null;
}


function firstString(
    ...values: unknown[]
): string {
    return (
        values.find(
            value =>
                typeof value
                === "string"
        ) as string | undefined
    )
        ?? "";
}


function booleanValue(
    value: unknown
): boolean {
    return (
        typeof value === "boolean"
        ? value
        : false
    );
}


function numberString(
    value: unknown,
    fallback = ""
): string {
    return (
        typeof value === "number"
        && Number.isFinite(
            value
        )
    )
        ? String(
            value
        )
        : fallback;
}


function parseInteger(
    value: string
): number | null {
    const normalized =
        value.trim();

    if (
        !/^\d+$/u.test(
            normalized
        )
    ) {
        return null;
    }

    const parsed =
        Number(
            normalized
        );

    return (
        Number.isSafeInteger(
            parsed
        )
        ? parsed
        : null
    );
}


function parsePosition(
    value: string
): number {
    return (
        parseInteger(
            value
        )
        ?? 0
    );
}


function optionalText(
    value: string
): string | undefined {
    const normalized =
        value.trim();

    return (
        normalized
        || undefined
    );
}


function compactObject(
    value: Record<string, Json | undefined>
): Record<string, Json> {
    return Object.fromEntries(
        Object.entries(
            value
        ).filter(
            ([, item]) =>
                item !== undefined
        )
    ) as Record<string, Json>;
}


/* -------------------------------------------------------------------------- */
/* Exports                                                                    */
/* -------------------------------------------------------------------------- */

export {
    applyHomeFeatureKindDefaults,
    buildAdminHomeFeaturePayload,
    createEmptyAdminHomeFeature,
    homeFeatureIdPattern,
    homeFeatureKinds,
    readAdminHomeFeatureEditor,
    updateAdminHomeFeatureEditor,
    validateAdminHomeFeatureEditor
};

export type {
    AdminHomeFeatureEditorValue,
    AdminHomeFeatureValidationIssue,
    HomeFeatureKind
};